import { useState, useContext } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { UserContext } from "@/App";
import { useNotifications } from "@/components/NotificationProvider";
import { useLanguage } from "@/components/LanguageProvider";
import { 
  Home, Gamepad2, Plane, CircleDot, Dices, Cherry, 
  Newspaper, Sparkles, User, Trophy, LogOut, Menu, X,
  Wallet, ChevronDown, Bell, Search, Zap, CreditCard,
  MessageCircle, Bomb, Target, Coins, Gift, Citrus,
  TrendingUp, Hash, Crown, Users, Percent, ArrowDownCircle, Shield, Settings, Globe
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const gameItems = [
  { path: "/games/aviator", label: "Aviator", icon: Plane, hot: true },
  { path: "/games/crash", label: "Crash", icon: TrendingUp, hot: true },
  { path: "/games/coinlemon", label: "Coin on Lemon", icon: Citrus },
  { path: "/games/limbo", label: "Limbo", icon: Target },
  { path: "/games/keno", label: "Keno", icon: Hash },
  { path: "/games/dice", label: "Dice", icon: Dices },
  { path: "/games/slots", label: "Slots", icon: Cherry, hot: true },
  { path: "/games/mines", label: "Mines", icon: Bomb },
  { path: "/games/plinko", label: "Plinko", icon: CircleDot },
  { path: "/games/wheel", label: "Wheel", icon: Coins },
  { path: "/games/coinbalance", label: "Coin Balance", icon: CircleDot },
];

const featureItems = [
  { path: "/news", label: "Predictions", icon: Newspaper },
  { path: "/tournaments", label: "Tournaments", icon: Trophy, hot: true },
  { path: "/ai-creator", label: "AI Creator", icon: Sparkles },
  { path: "/leaderboard", label: "Leaderboard", icon: Trophy },
  { path: "/chat", label: "Chat Room", icon: MessageCircle },
];

const rewardItems = [
  { path: "/vip", label: "VIP Rewards", icon: Crown, hot: true },
  { path: "/referral", label: "Refer & Earn", icon: Users },
  { path: "/promos", label: "Promotions", icon: Percent, hot: true },
  { path: "/withdraw", label: "Withdraw", icon: ArrowDownCircle },
];

export const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showSupport, setShowSupport] = useState(false);
  
  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/80 z-40 md:hidden"
          onClick={onClose}
        />
      )}
      
      <aside className={`
        fixed left-0 top-0 bottom-0 w-64 bg-[#09090A] border-r border-white/[0.05] z-50
        transform transition-transform duration-300 flex flex-col
        ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="h-16 flex items-center px-5 border-b border-white/[0.05] shrink-0">
          <Link to="/dashboard" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#00E701] flex items-center justify-center shadow-[0_0_20px_rgba(0,231,1,0.4)]">
              <Gamepad2 className="w-5 h-5 text-black" />
            </div>
            <span className="text-xl font-bold text-white font-['Rajdhani']">BetIndia</span>
          </Link>
          <button 
            onClick={onClose}
            className="ml-auto md:hidden text-white/60 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Navigation - Scrollable */}
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {/* Lobby */}
          <Link
            to="/dashboard"
            onClick={onClose}
            className={`
              flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200
              ${location.pathname === '/dashboard'
                ? 'bg-[#00E701]/10 text-[#00E701]' 
                : 'text-white/60 hover:bg-white/5 hover:text-white'
              }
            `}
          >
            <Home className="w-5 h-5" />
            <span className="font-medium text-sm">Lobby</span>
          </Link>

          <div className="text-[10px] uppercase text-white/30 font-bold px-3 py-2 mt-2">Casino Games</div>
          {gameItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200
                  ${isActive 
                    ? 'bg-[#00E701]/10 text-[#00E701]' 
                    : 'text-white/60 hover:bg-white/5 hover:text-white'
                  }
                `}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium text-sm">{item.label}</span>
                {item.hot && (
                  <span className="ml-auto px-1.5 py-0.5 rounded text-[9px] bg-[#FF3B30] text-white font-bold">
                    HOT
                  </span>
                )}
              </Link>
            );
          })}
          
          <div className="text-[10px] uppercase text-white/30 font-bold px-3 py-2 mt-4">Features</div>
          {featureItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200
                  ${isActive 
                    ? 'bg-[#00E701]/10 text-[#00E701]' 
                    : 'text-white/60 hover:bg-white/5 hover:text-white'
                  }
                `}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium text-sm">{item.label}</span>
                {item.hot && (
                  <span className="ml-auto px-1.5 py-0.5 rounded text-[9px] bg-[#FF3B30] text-white font-bold">
                    HOT
                  </span>
                )}
              </Link>
            );
          })}
          
          <div className="text-[10px] uppercase text-white/30 font-bold px-3 py-2 mt-4">Rewards</div>
          {rewardItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200
                  ${isActive 
                    ? 'bg-[#FFD600]/10 text-[#FFD600]' 
                    : 'text-white/60 hover:bg-white/5 hover:text-white'
                  }
                `}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium text-sm">{item.label}</span>
                {item.hot && (
                  <span className="ml-auto px-1.5 py-0.5 rounded text-[9px] bg-[#FFD600] text-black font-bold">
                    NEW
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
        
        {/* Bottom Actions - Fixed */}
        <div className="shrink-0 p-3 border-t border-white/[0.05] space-y-2">
          {/* Buy Credits */}
          <button
            onClick={() => { onClose(); navigate('/profile'); }}
            className="w-full flex items-center gap-3 px-3 py-3 rounded-xl bg-gradient-to-r from-[#FFD600]/20 to-[#FF6B00]/10 border border-[#FFD600]/30 text-[#FFD600] hover:from-[#FFD600]/30 transition-all"
          >
            <CreditCard className="w-5 h-5" />
            <span className="font-bold text-sm">Buy Credits</span>
            <Gift className="w-4 h-4 ml-auto" />
          </button>
          
          {/* Live Support - Now links to support page */}
          <button
            onClick={() => { onClose(); navigate('/support'); }}
            className="w-full flex items-center gap-3 px-3 py-3 rounded-xl bg-[#007AFF]/10 border border-[#007AFF]/30 text-[#007AFF] hover:bg-[#007AFF]/20 transition-all"
          >
            <MessageCircle className="w-5 h-5" />
            <span className="font-bold text-sm">Live Support</span>
            <div className="ml-auto w-2 h-2 rounded-full bg-[#00E701] animate-pulse" />
          </button>
          
          <p className="text-[9px] text-white/30 text-center pt-2">
            18+ | Play Responsibly
          </p>
        </div>
      </aside>
    </>
  );
};

export const Header = ({ onMenuClick }) => {
  const { user, logout } = useContext(UserContext);
  const navigate = useNavigate();
  const notifications = useNotifications();
  const [showNotifications, setShowNotifications] = useState(false);
  
  const handleLogout = async () => {
    await logout();
    navigate('/');
  };
  
  return (
    <header className="sticky top-0 z-30 h-16 bg-[#0F0F10]/95 backdrop-blur-xl border-b border-white/[0.05]">
      <div className="h-full px-4 flex items-center gap-4">
        {/* Left: Menu button (mobile) */}
        <button 
          onClick={onMenuClick}
          className="md:hidden p-2 text-white/60 hover:text-white rounded-lg hover:bg-white/5"
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-5 h-5" />
        </button>
        
        {/* Search */}
        <div className="hidden md:flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 flex-1 max-w-md">
          <Search className="w-4 h-4 text-white/40" />
          <input 
            type="text"
            placeholder="Search games..."
            className="bg-transparent border-none outline-none text-white text-sm placeholder:text-white/40 w-full"
          />
        </div>
        
        <div className="flex-1" />
        
        {/* Right: Actions */}
        <div className="flex items-center gap-2">
          {/* Notifications */}
          <DropdownMenu open={showNotifications} onOpenChange={setShowNotifications}>
            <DropdownMenuTrigger asChild>
              <button className="p-2 text-white/60 hover:text-white rounded-lg hover:bg-white/5 relative">
                <Bell className="w-5 h-5" />
                {notifications?.unreadCount > 0 && (
                  <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#FF3B30] flex items-center justify-center text-white text-[10px] font-bold">
                    {notifications.unreadCount > 9 ? '9+' : notifications.unreadCount}
                  </div>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 bg-[#1A1B1F] border-white/10 p-0 max-h-96 overflow-y-auto">
              <div className="p-3 border-b border-white/10 flex items-center justify-between">
                <span className="text-white font-semibold text-sm">Notifications</span>
                {notifications?.unreadCount > 0 && (
                  <button 
                    onClick={() => notifications?.markAllAsRead()}
                    className="text-xs text-[#007AFF] hover:underline"
                  >
                    Mark all read
                  </button>
                )}
              </div>
              {notifications?.notifications?.length === 0 ? (
                <div className="p-8 text-center text-white/40 text-sm">
                  No notifications yet
                </div>
              ) : (
                notifications?.notifications?.slice(0, 10).map((notif) => (
                  <div
                    key={notif.id}
                    onClick={() => notifications?.markAsRead(notif.id)}
                    className={`p-3 border-b border-white/5 hover:bg-white/5 cursor-pointer ${
                      !notif.read ? 'bg-white/[0.02]' : ''
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {!notif.read && <div className="w-2 h-2 rounded-full bg-[#007AFF] mt-1.5 shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium">{notif.title}</p>
                        <p className="text-white/60 text-xs mt-0.5">{notif.message}</p>
                        <p className="text-white/30 text-[10px] mt-1">
                          {new Date(notif.timestamp).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Language Switcher */}
          <LanguageSwitcher />
          
          {/* Wallet */}
          <button 
            onClick={() => navigate('/profile')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#00E701]/10 border border-[#00E701]/20 hover:bg-[#00E701]/20 transition-colors"
            data-testid="wallet-balance"
          >
            <Wallet className="w-4 h-4 text-[#00E701]" />
            <span className="font-mono font-bold text-[#00E701]">
              ₹{(user?.balance || 0).toLocaleString('en-IN')}
            </span>
          </button>
          
          {/* Buy Credits */}
          <Button 
            onClick={() => navigate('/profile')}
            className="hidden sm:flex bg-[#FFD600] hover:bg-[#E6C200] text-black font-bold px-4 rounded-lg"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Buy Credits
          </Button>
          
          {/* Profile Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className="flex items-center gap-2 px-2 py-2 hover:bg-white/5 rounded-lg"
                data-testid="profile-dropdown"
              >
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-white/20" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FFD600] to-[#FF6B00] flex items-center justify-center text-black font-bold text-sm">
                    {user?.name?.charAt(0) || 'U'}
                  </div>
                )}
                <ChevronDown className="w-4 h-4 text-white/40" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 bg-[#1A1B1F] border-white/10">
              <DropdownMenuItem 
                onClick={() => navigate('/profile')}
                className="text-white/80 hover:text-white hover:bg-white/5 cursor-pointer"
              >
                <User className="w-4 h-4 mr-2" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => navigate('/withdraw')}
                className="text-white/80 hover:text-white hover:bg-white/5 cursor-pointer"
              >
                <ArrowDownCircle className="w-4 h-4 mr-2" />
                Withdraw
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => navigate('/responsible-gaming')}
                className="text-white/80 hover:text-white hover:bg-white/5 cursor-pointer"
              >
                <Shield className="w-4 h-4 mr-2" />
                Responsible Gaming
              </DropdownMenuItem>
              {user?.is_admin && (
                <DropdownMenuItem 
                  onClick={() => navigate('/admin')}
                  className="text-[#FF3B30] hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 cursor-pointer"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Admin Panel
                </DropdownMenuItem>
              )}
              <DropdownMenuItem 
                onClick={handleLogout}
                className="text-[#FF3B30] hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 cursor-pointer"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  return (
    <div className="min-h-screen bg-[#0F0F10]">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="md:ml-64">
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <main className="p-4 md:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

// Language Switcher Component
const LanguageSwitcher = () => {
  const { language, changeLanguage } = useLanguage() || {};
  
  if (!changeLanguage) return null;
  
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="p-2 text-white/60 hover:text-white rounded-lg hover:bg-white/5 flex items-center gap-1">
          <Globe className="w-4 h-4" />
          <span className="text-xs font-medium uppercase hidden sm:inline">{language}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-32 bg-[#1A1B1F] border-white/10">
        <DropdownMenuItem 
          onClick={() => changeLanguage('en')}
          className={`text-white/80 hover:text-white hover:bg-white/5 cursor-pointer ${language === 'en' ? 'bg-white/10' : ''}`}
        >
          🇺🇸 English
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => changeLanguage('hi')}
          className={`text-white/80 hover:text-white hover:bg-white/5 cursor-pointer ${language === 'hi' ? 'bg-white/10' : ''}`}
        >
          🇮🇳 हिंदी
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

